import {
  classifyCounselingIntent,
  CounselingIntent,
  IntakeFormData,
  IntakeFormUserInputFields,
} from "@app/service/intakeForm";
import { CancerStage } from "@app/provider/types";
import {
  ONCOLOGY_TEST_TYPES,
  ParsedTestCard,
  TestCardStatus,
  TestResultCode,
  TestType,
} from "@app/provider/testData/types";
import acuityData from "@app/pages/private/testResult/details/acuity_map.json";
import { COUNTRIES, LANGUAGES } from "@app/utils/intakeFormOptions";

export const ACUITY_BASE_URL = "https://app.acuityscheduling.com/schedule.php";

/**
 * Acuity custom field IDs for the GIS appointment types.
 *
 * The ONC and WH (Women's Health / Empower) appointment types are different
 * Acuity forms with overlapping but non-identical field sets, so each flow
 * has its own ID map. Fields not present on a given flow are simply absent
 * from that flow's map; the URL builder skips them via `addXField` helpers
 * that no-op on `undefined` field IDs.
 *
 * Field type reference (determines URL parameter format):
 *   text     → field:ID=value
 *   multibox → field:ID[]=value  (repeated for each selected option)
 *   radio    → field:ID=value
 *   checkbox → field:ID=yes
 */
export type AcuityFlow = "ONC" | "WH";

/**
 * Union of every custom field across both ONC and WH GIS Acuity forms.
 * All keys are optional so a flow's map can declare only the fields it has.
 */
export interface AcuityFieldIds {
  // Shared (different IDs per flow)
  dateOfBirth?: number;
  physicianClinic?: number;
  orderingPhysician?: number;
  caseId?: number;
  countryOfResidence?: number;
  interpreterLanguage?: number;
  enteredBy?: number;

  // ONC-only
  cancerType?: number;
  /** Free-text companion to the "Cancer Type" multibox "Other" option. */
  cancerTypeOther?: number;
  cancerStage?: number;
  hasPreviouslyTakenTest?: number;
  hasActiveDisease?: number;
  reasonForAppointment?: number;
  interpreterNeeded?: number;

  // WH-only
  hasFamilyHistoryOfCancer?: number;
  stateOfResidence?: number;
  howDidYouHearAboutNatera?: number;
  additionalInfoOrQuestions?: number;

  consent: {
    patientMustBePresent?: number;
    emailConsent?: number;
    noRecordingConsent?: number;
    /** ONC-only — WH form has no separate privacy-policy consent checkbox. */
    privacyPolicyConsent?: number;
  };
}

export const FIELD_IDS = {
  dateOfBirth: 12607049, // text
  physicianClinic: 12607054, // text
  orderingPhysician: 12607051, // text
  caseId: 12607057, // text
  cancerType: 13224873, // multibox
  cancerTypeOther: 13224890, // text — free-text value when "Other" is selected
  cancerStage: 13224893, // multibox
  hasPreviouslyTakenTest: 12607055, // radio
  hasActiveDisease: 12607106, // multibox
  reasonForAppointment: 12607068, // multibox
  countryOfResidence: 12607117, // text
  interpreterNeeded: 12607118, // multibox
  interpreterLanguage: 12607121, // text
  consent: {
    patientMustBePresent: 12607156, // checkbox
    emailConsent: 12607157, // checkbox
    noRecordingConsent: 12607159, // checkbox
    privacyPolicyConsent: 12607160, // checkbox
  },
  enteredBy: 12607153, // text
} as const;

/** CounselingIntent → Acuity counseling-intent label */
export const COUNSELING_INTENT_LABELS: Record<CounselingIntent, string> = {
  "pre-test": "Pre-Test",
  "post-test": "Post-Test",
};

export const WH_FIELD_IDS = {
  dateOfBirth: 7321255, // text
  orderingPhysician: 7321256, // text
  physicianClinic: 7321257, // text
  caseId: 7321258, // text
  hasFamilyHistoryOfCancer: 11354946, // text
  stateOfResidence: 10402662, // text
  countryOfResidence: 7321259, // text
  interpreterLanguage: 7321261, // text
  howDidYouHearAboutNatera: 7321262, // text
  additionalInfoOrQuestions: 7321263, // text
  enteredBy: 7321264, // text
  consent: {
    patientMustBePresent: 7321265, // checkbox
    emailConsent: 7321266, // checkbox
    noRecordingConsent: 7321267, // checkbox
    // No privacyPolicyConsent on the WH form
  },
} as const;

export function getAcuityFieldIdsForFlow(flow: AcuityFlow): AcuityFieldIds {
  switch (flow) {
    case "ONC":
      return FIELD_IDS;
    case "WH":
      return WH_FIELD_IDS;
  }
}

/**
 * Resolves the Acuity flow for a given test type. Oncology products other than
 * EMPOWER use the ONC form. EMPOWER and non-oncology products use the WH form.
 * An unknown type keeps the legacy ONC fallback.
 */
export function getAcuityFlow(
  testType: TestType | string | undefined
): AcuityFlow {
  if (!testType) return "ONC";
  const isOncology =
    ONCOLOGY_TEST_TYPES.includes(testType as TestType) &&
    testType !== TestType.EMPOWER;
  return isOncology ? "ONC" : "WH";
}
// ---------------------------------------------------------------------------
// Acuity multibox answer-label mappings
// Values must match the option labels configured in Acuity exactly.
// ---------------------------------------------------------------------------

/**
 * Maps known cancer-type strings to their Acuity "Cancer Type" multibox label.
 * Values not listed here fall back to "Other".
 */
export const CANCER_TYPE_LABELS: Record<string, string> = {
  COLORECTAL: "Colon or Rectal",
  BREAST: "Breast",
  OVARIAN: "Ovarian",
  ENDOMETRIAL: "Endometrial or Uterine",
  MELANOMA: "Melanoma",
  MELANOMA_MUCOSAL: "Melanoma",
  MELANOMA_UVEAL: "Melanoma",
  ESOPHAGEAL: "Esophageal",
  LUNG: "Lung",
  PROSTATE: "Prostate",
  GASTRIC: "Gastric or Stomach",
  STOMACH: "Gastric or Stomach",
  SARCOMA: "Sarcoma",
  LYMPHOMA: "Lymphoma",
  UTERINE_SARCOMA: "Sarcoma",
};

export interface ResolvedCancerType {
  /**
   * Value for the "Cancer Type" multibox (field `cancerType`): a known option
   * label, or "Other". `undefined` when there is no cancer type to send.
   */
  label: string | undefined;
  /**
   * Free-text value for the separate "Other cancer type" field
   * (`cancerTypeOther`), populated only when the patient typed a value that has
   * no matching multibox option.
   */
  otherText?: string;
}

/**
 * Resolves the Acuity values for a cancer-type string.
 *
 * Known cancer-type codes map to their configured "Cancer Type" multibox label.
 * When `allowFreeText` is set (the no-order intake flow, where the patient can
 * type a cancer type via the "Other" option), an unrecognized value is the
 * patient's own free text: the multibox is set to "Other" and the typed text is
 * returned separately so it can be sent to the companion "Other cancer type"
 * field. Without it, that text is dropped entirely.
 *
 * Order-backed clinical codes that have no Acuity option (e.g. "PANCREATIC")
 * still fall back to a bare "Other": those values are internal codes, not
 * patient-readable text.
 */
export function resolveCancerTypeAcuityValue(
  cancerType: string | null | undefined,
  allowFreeText: boolean
): ResolvedCancerType {
  if (!cancerType) return { label: undefined };
  const trimmed = cancerType.trim();
  if (!trimmed) return { label: undefined };
  const known = CANCER_TYPE_LABELS[trimmed.toUpperCase()];
  if (known) return { label: known };
  if (allowFreeText)
    return { label: "Other", otherText: trimmed.slice(0, 255) };
  return { label: "Other" };
}

/** CancerStage enum → Acuity "Cancer Stage" multibox label */
export const CANCER_STAGE_LABELS: Record<CancerStage, string> = {
  [CancerStage.I]: "I",
  [CancerStage.II]: "II",
  [CancerStage.IIA]: "II",
  [CancerStage.IIB]: "II",
  [CancerStage.IIC]: "II",
  [CancerStage.III]: "III",
  [CancerStage.IIIA]: "III",
  [CancerStage.IIIB]: "III",
  [CancerStage.IIIC]: "III",
  [CancerStage.IV]: "IV (metastatic)",
  [CancerStage.OTHER]: "I do not have cancer",
};

/** Intake reason code → Acuity "Reason for Appointment" multibox label */
export const REASON_LABELS: Record<string, string> = {
  signatera: "I want to discuss my Signatera test results",
  altera: "I want to discuss my Altera test results",
  latitude: "I want to discuss my Latitude test results",
};

/** hasActiveDisease boolean → Acuity multibox label */
export const ACTIVE_DISEASE_LABELS: Record<string, string> = {
  true: "Active disease",
  false: "Remission",
};

/** interpreterNeeded boolean → Acuity multibox label */
export const INTERPRETER_NEEDED_LABELS: Record<string, string> = {
  true: "Yes (Please make sure an interpreter was requested on the previous page)",
  false: "No",
};
// ---------------------------------------------------------------------------
// Helpers – each appends zero or more `field:ID=value` segments to `parts`
// ---------------------------------------------------------------------------

const encode = (value: string) => encodeURIComponent(value);

function addTextField(
  parts: string[],
  fieldId: number | undefined,
  value: string | null | undefined
): void {
  if (fieldId == null || !value) return;
  parts.push(`field:${fieldId}=${encode(value)}`);
}

function addMultiboxField(
  parts: string[],
  fieldId: number | undefined,
  values: string[]
): void {
  if (fieldId == null) return;
  for (const v of values) {
    if (v) {
      parts.push(`field:${fieldId}[]=${encode(v)}`);
    }
  }
}

function addRadioField(
  parts: string[],
  fieldId: number | undefined,
  value: string | null | undefined
): void {
  if (fieldId == null || !value) return;
  parts.push(`field:${fieldId}=${encode(value)}`);
}

function addCheckboxField(
  parts: string[],
  fieldId: number | undefined,
  checked: boolean | null | undefined
): void {
  if (fieldId == null || !checked) return;
  parts.push(`field:${fieldId}=${encode("yes")}`);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolves the Acuity owner ID, scheduling-source field ID, and counseling
 * intent field ID based on the test type.
 *
 * Counseling intent field IDs differ between Acuity calendars:
 *   - Oncology calendar     → 18565637
 *   - WH / Empower calendar → 18565665
 */
export function getAcuityOwnerConfig(testType: TestType | string | undefined): {
  owner: string;
  schedulingSourceFieldId: string;
  counselingIntentFieldId: string;
} {
  if (testType === TestType.EMPOWER) {
    // Empower always uses the WH (Women's Health) owner
    return {
      owner: "11361661",
      schedulingSourceFieldId: "17697279",
      counselingIntentFieldId: "18565665",
    };
  }

  const isOncology =
    testType &&
    ONCOLOGY_TEST_TYPES.includes(testType as TestType) &&
    testType !== TestType.EMPOWER;

  return {
    owner: isOncology ? "27869891" : "11361661",
    schedulingSourceFieldId: isOncology ? "17904034" : "17697279",
    counselingIntentFieldId: isOncology ? "18565637" : "18565665",
  };
}

const ACUITY_PANORAMA_LOW_RISK = "PANORAMA_LOW_RISK";
export const ACUITY_RESULT_CODE_WILDCARD = "*";
export type AcuityAppointmentConfig = (typeof acuityData)[number];

function isReleasedResultStatus(
  status?: TestCardStatus | string | null
): boolean {
  return (
    status === TestCardStatus.RESULT_READY ||
    status === TestCardStatus.TEST_COMPLETE
  );
}

function hasResultCodeMatch(
  entryResultCode: string | undefined,
  resultCode?: string | null
): boolean {
  return Boolean(resultCode && entryResultCode === resultCode);
}

function hasWildcardResultCode(entryResultCode: string | undefined): boolean {
  return entryResultCode === ACUITY_RESULT_CODE_WILDCARD;
}

export interface AcuityLookupTypeParams {
  testType: TestType | string | undefined;
  status?: TestCardStatus | string | null;
  resultCode?: string | null;
  /**
   * Pre-resolved counseling intent, for callers holding a raw SOS sales status
   * (the anonymous order-lookup funnel) that `isReleasedResultStatus` can't
   * classify — a released Signatera result reports `TNP`.
   */
  counselingIntent?: CounselingIntent;
}

/**
 * Resolves the `acuity_map.json` lookup key for result-specific appointment
 * types. Most products use their test type directly; released low-risk
 * Panorama results use a synthetic map entry for the low-risk discussion.
 */
export function resolveAcuityLookupType({
  testType,
  status,
  resultCode,
  counselingIntent,
}: AcuityLookupTypeParams): TestType | string | undefined {
  const isReleased =
    counselingIntent === "post-test" || isReleasedResultStatus(status);
  const isPanoramaLowRisk =
    testType === TestType.PANORAMA &&
    resultCode === TestResultCode.LOW_RISK_RESULT &&
    isReleased;

  return isPanoramaLowRisk ? ACUITY_PANORAMA_LOW_RISK : testType;
}

/**
 * Empower's negative and carrier post-test discussions are booked on dedicated
 * Acuity pages that play the result video before the calendar. Those pages are
 * only reachable through their own link, so they can't be expressed as an
 * `appointmentType` on the generic scheduler. Every other product — including
 * Panorama's low-risk discussion — books through the generic scheduler.
 */
export function getResultSpecificAcuityDirectLink(
  testType: TestType | string | undefined,
  appointmentConfig: AcuityAppointmentConfig | null
): string | undefined {
  if (testType !== TestType.EMPOWER) return undefined;
  const entryResultCode = appointmentConfig?.resultCode;
  if (!entryResultCode || entryResultCode === ACUITY_RESULT_CODE_WILDCARD) {
    return undefined;
  }
  return appointmentConfig?.acuityDirectLink;
}

/**
 * Looks up the Acuity appointment-type ID for a given test type from
 * `acuity_map.json`. Returns `null` when no mapping exists.
 *
 * A test type can have multiple entries differentiated by `counselingIntent`
 * (e.g. Empower has separate Pre-Test and Post-Test appointment types/frames).
 * Some post-test entries are further differentiated by `resultCode` (e.g.
 * Empower negative/carrier post-test discussions). When both `intent` and
 * `resultCode` are supplied we prefer that exact match; otherwise we fall back
 * to the wildcard entry for the intent and then the first entry for the test.
 *
 * Used by the public GIS triage page to detect "no GIS available for this
 * product" before attempting to render the WH/OH Acuity iframe — otherwise
 * the iframe URL would be built without an `appointmentType` param and the
 * user would land on a broken scheduler view.
 */
export function getAcuityAppointmentConfig(
  testType: TestType | string | undefined,
  intent?: CounselingIntent,
  resultCode?: string | null
): AcuityAppointmentConfig | null {
  if (!testType) return null;

  const entries = acuityData.filter((item) => item.testType === testType);
  if (entries.length === 0) return null;

  if (intent) {
    const intentLabel = COUNSELING_INTENT_LABELS[intent];
    if (resultCode) {
      const resultCodeMatch = entries.find(
        (item) =>
          item.counselingIntent === intentLabel &&
          hasResultCodeMatch(item.resultCode, resultCode)
      );
      if (resultCodeMatch) return resultCodeMatch;
    }

    const intentMatch = entries.find(
      (item) =>
        item.counselingIntent === intentLabel &&
        (hasWildcardResultCode(item.resultCode) || !("resultCode" in item))
    );
    if (intentMatch) return intentMatch;
  }

  return entries[0];
}

export function getAcuityAppointmentTypeId(
  testType: TestType | string | undefined,
  intent?: CounselingIntent,
  resultCode?: string | null
): string | null {
  return (
    getAcuityAppointmentConfig(
      testType,
      intent,
      resultCode
    )?.acuityAppointmentTypeID.toString() ?? null
  );
}

export interface AcuityGisIframeUrlParams {
  /** Test type of the auto-selected order — drives owner, appointment type, and field IDs. */
  testType: TestType | string | undefined;
  /** Test card status — classified into pre-test / post-test for the counseling-intent field. */
  status?: TestCardStatus | string | null;
  /**
   * Pre-resolved counseling intent. Callers that only have a raw SOS sales
   * status (the anonymous order-lookup funnel) must pass the server-resolved
   * value, since `status` cannot be classified client-side.
   */
  counselingIntent?: CounselingIntent;
  /** Result code from LIMS/SOS, used for result-specific appointment types. */
  resultCode?: string | null;
  /**
   * Value to emit for Acuity's scheduling-source tracking field. Examples:
   *   - "Public_Entry_UPP_Auth" (Work Item 3 — public GIS triage entry)
   *   - "testResultDetailsPage" (legacy test-result page entry)
   *   - "patientEducationPage" (legacy patient-education page entry)
   */
  schedulingSourceValue: string;
}

/**
 * Builds the Acuity scheduling iframe URL for a GIS entry point that does
 * *not* go through the full intake form (e.g. WH / OH orders, or the public
 * triage page). Emits only the four params Acuity needs to land the user on
 * the correct calendar with correct tracking:
 *   - owner + appointmentType (from `getAcuityOwnerConfig` + `acuity_map.json`)
 *   - scheduling-source custom field (caller-supplied value)
 *   - counseling-intent custom field (derived from test status)
 *
 * For the full-intake URL with pre-filled patient/clinical data use
 * `buildAcuitySchedulingUrl` instead.
 */
export function buildAcuityGisIframeUrl({
  testType,
  status,
  counselingIntent: providedCounselingIntent,
  resultCode,
  schedulingSourceValue,
}: AcuityGisIframeUrlParams): string {
  const { owner, schedulingSourceFieldId, counselingIntentFieldId } =
    getAcuityOwnerConfig(testType);

  const counselingIntent =
    providedCounselingIntent ?? classifyCounselingIntent(status);
  const acuityLookupType = resolveAcuityLookupType({
    testType,
    status,
    resultCode,
    counselingIntent,
  });

  const appointmentConfig = getAcuityAppointmentConfig(
    acuityLookupType,
    counselingIntent,
    resultCode
  );
  const appointmentTypeId =
    appointmentConfig?.acuityAppointmentTypeID.toString() ?? "";

  const counselingIntentLabel = COUNSELING_INTENT_LABELS[counselingIntent];
  const trackingFields = [
    `field:${schedulingSourceFieldId}=${encode(schedulingSourceValue)}`,
    `field:${counselingIntentFieldId}=${encode(counselingIntentLabel)}`,
  ];

  const directLink = getResultSpecificAcuityDirectLink(
    testType,
    appointmentConfig
  );
  if (directLink) {
    // The link's slug already pins the owner and appointment type.
    return `${directLink}?${trackingFields.join("&")}`;
  }

  const parts = [
    `owner=${encode(owner)}`,
    appointmentTypeId && `appointmentType=${encode(appointmentTypeId)}`,
    ...trackingFields,
  ].filter(Boolean);

  return `${ACUITY_BASE_URL}?${parts.join("&")}`;
}

export interface AcuityUrlParams {
  /** The patient's latest test card — used to resolve owner, appointment type, etc. */
  latestTest: ParsedTestCard | null;
  /** LIMS case ID to prefill into Acuity's Case ID custom field. */
  caseId?: number | string | null;
  /** Pre-filled data from the backend (patient, order, test info) */
  intakeFormData?: IntakeFormData | null;
  /** User-provided fields collected during the intake form flow */
  userInputFields?: Partial<IntakeFormUserInputFields> | null;
  /** Phone confirmed/edited by the user in step 1 (overrides intakeFormData.phone) */
  confirmedPhone?: string | null;
  /** Email confirmed/edited by the user in step 1 (overrides intakeFormData.email) */
  confirmedEmail?: string | null;
  /** GIS flow type from the triage rule engine — overrides test-type-based routing when set to "WH_EMPOWER" */
  triageGisFlowType?: string | null;
  /**
   * Explicit test type to resolve the Acuity flow/owner/appointment from,
   * overriding both `latestTest` and the `triageGisFlowType` heuristic. Used by
   * the no-order GIS funnel, which has no backing `latestTest` but still lets
   * the patient pick the test (e.g. Empower → WH calendar, Signatera → ONC).
   */
  testTypeOverride?: TestType | string | null;
  /**
   * Pre-resolved counseling intent, overriding the classification of
   * `latestTest.status`. Required by callers with no test card to classify (the
   * no-order funnel) or holding a raw SOS sales status (the anonymous
   * order-lookup funnel), where a released Signatera result reports `TNP`.
   */
  counselingIntent?: CounselingIntent;
}

/**
 * Builds a fully-formed Acuity Scheduling URL with pre-filled fields.
 *
 * Derives the Acuity owner, appointment type ID, and scheduling-source
 * field ID from the `latestTest`. The URL includes:
 *  - Core scheduling params (owner, appointmentType)
 *  - Scheduling source tracking field
 *  - Acuity built-in contact fields (firstName, lastName, email, phone)
 *  - Custom fields mapped to their Acuity field IDs
 *
 * Null / undefined values are silently skipped so the function works with
 * partial data (e.g. when only pre-filled data is available).
 */
export function buildAcuitySchedulingUrl({
  latestTest,
  caseId,
  intakeFormData,
  userInputFields,
  confirmedPhone,
  confirmedEmail,
  triageGisFlowType,
  testTypeOverride,
  counselingIntent: providedCounselingIntent,
}: AcuityUrlParams): string {
  const effectiveTestType =
    testTypeOverride ??
    (triageGisFlowType === "WH_EMPOWER"
      ? TestType.EMPOWER
      : latestTest?.testType);

  const flow = getAcuityFlow(effectiveTestType);
  const fields = getAcuityFieldIdsForFlow(flow);

  const { owner, schedulingSourceFieldId } =
    getAcuityOwnerConfig(effectiveTestType);

  const counselingIntent =
    providedCounselingIntent ?? classifyCounselingIntent(latestTest?.status);
  // Result-specific appointment types (Empower negative/carrier, Panorama
  // low-risk) are keyed by result code alone, so a code only means anything
  // for the product actually being scheduled. When triage reroutes an oncology
  // patient to the Empower calendar, their Signatera "NEGATIVE" would
  // otherwise book the Empower negative-result discussion.
  const resultCode =
    latestTest?.testType === effectiveTestType ? latestTest?.resultCode : null;
  const acuityLookupType = resolveAcuityLookupType({
    testType: effectiveTestType ?? undefined,
    status: latestTest?.status,
    resultCode,
    counselingIntent,
  });

  const appointmentTypeId: string =
    getAcuityAppointmentTypeId(
      acuityLookupType,
      counselingIntent,
      resultCode
    ) ?? "";

  if (!appointmentTypeId) {
    console.warn(
      `[buildAcuitySchedulingUrl] No Acuity appointment type found for testType="${latestTest?.testType ?? "null"}". 
      The generated URL may not work.`
    );
  }

  const parts: string[] = [];

  // -- Core Acuity params --------------------------------------------------
  parts.push(`owner=${encode(owner)}`);
  parts.push(`appointmentType=${encode(appointmentTypeId)}`);

  // -- Scheduling source tracking ------------------------------------------
  parts.push(
    `field:${schedulingSourceFieldId}=${encode("testResultDetailsPage")}`
  );

  // -- Acuity built-in (non-custom) contact fields -------------------------
  // Prefer user-confirmed values from step 1 over upstream pre-filled data
  if (intakeFormData?.firstName) {
    parts.push(`firstName=${encode(intakeFormData.firstName)}`);
  }
  if (intakeFormData?.lastName) {
    parts.push(`lastName=${encode(intakeFormData.lastName)}`);
  }
  const email = confirmedEmail || intakeFormData?.email;
  if (email) {
    parts.push(`email=${encode(email)}`);
  }
  const phone = confirmedPhone || intakeFormData?.phone;
  if (phone) {
    parts.push(`phone=${encode(phone)}`);
  }

  // -- Custom fields: pre-filled data --------------------------------------
  // Calls against fields not present on the resolved flow's field map are
  // silently skipped by the addXField helpers (e.g. cancerType/Stage on WH).
  const patientName = [intakeFormData?.firstName, intakeFormData?.lastName]
    .filter(Boolean)
    .join(" ");
  addTextField(parts, fields.enteredBy, patientName || undefined);

  addTextField(parts, fields.dateOfBirth, intakeFormData?.dateOfBirth);
  addTextField(parts, fields.physicianClinic, intakeFormData?.physicianClinic);
  addTextField(
    parts,
    fields.orderingPhysician,
    intakeFormData?.orderingPhysician
  );
  addTextField(parts, fields.caseId, caseId?.toString());

  // Order-backed flow only: the value is a clinical code, so free-text
  // passthrough is disabled (unknown codes fall back to "Other").
  const resolvedCancerType = resolveCancerTypeAcuityValue(
    intakeFormData?.cancerType,
    true
  );
  if (resolvedCancerType.label) {
    addMultiboxField(parts, fields.cancerType, [resolvedCancerType.label]);
  }
  addTextField(parts, fields.cancerTypeOther, resolvedCancerType.otherText);
  if (intakeFormData?.cancerStage) {
    const label = CANCER_STAGE_LABELS[intakeFormData.cancerStage];
    if (label) {
      addMultiboxField(parts, fields.cancerStage, [label]);
    }
  }

  if (intakeFormData?.hasPreviouslyTakenTest != null) {
    addRadioField(
      parts,
      fields.hasPreviouslyTakenTest,
      intakeFormData.hasPreviouslyTakenTest ? "yes" : "no"
    );
  }

  // -- Custom fields: user-provided data -----------------------------------
  if (userInputFields) {
    // Active disease (boolean → multibox label)
    if (userInputFields.hasActiveDisease != null) {
      addMultiboxField(parts, fields.hasActiveDisease, [
        ACTIVE_DISEASE_LABELS[String(userInputFields.hasActiveDisease)],
      ]);
    }

    // Reason for appointment (comma-separated codes → Acuity multibox labels)
    if (userInputFields.reasonForAppointment) {
      const reasons = userInputFields.reasonForAppointment
        .split(",")
        .map((r) => r.trim())
        .map((r) => REASON_LABELS[r])
        .filter(Boolean);
      addMultiboxField(parts, fields.reasonForAppointment, reasons);
    }
    // Country of residence (text) — convert code back to readable label
    const countryLabel =
      COUNTRIES.find((c) => c.value === userInputFields.countryOfResidence)
        ?.label ?? userInputFields.countryOfResidence;
    addTextField(parts, fields.countryOfResidence, countryLabel);

    // Interpreter needed (boolean → multibox label)
    if (userInputFields.interpreterNeeded != null) {
      addMultiboxField(parts, fields.interpreterNeeded, [
        INTERPRETER_NEEDED_LABELS[String(userInputFields.interpreterNeeded)],
      ]);
    }

    // Interpreter language (text) — convert code back to readable label
    const languageLabel =
      LANGUAGES.find((l) => l.value === userInputFields.interpreterLanguage)
        ?.label ?? userInputFields.interpreterLanguage;
    addTextField(parts, fields.interpreterLanguage, languageLabel);

    // Consent checkboxes – fan a single acknowledgement out to all consent
    // checkboxes on the active flow's form. ONC has 4 (incl. privacy policy);
    // WH has 3. The undefined privacyPolicyConsent on WH is a no-op.
    if (userInputFields.consentAcknowledged) {
      addCheckboxField(parts, fields.consent.patientMustBePresent, true);
      addCheckboxField(parts, fields.consent.emailConsent, true);
      addCheckboxField(parts, fields.consent.noRecordingConsent, true);
      addCheckboxField(parts, fields.consent.privacyPolicyConsent, true);
    }
  }

  return `${ACUITY_BASE_URL}?${parts.join("&")}`;
}
